import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { authenticatedFetch } from "../../utils/api";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import ProgressBar from "../ProgressBar";
import { formatInteger, truncateText, PROGRAM_NAME_MAX_WIDTH, truncatedTextStyle } from "../../utils/formatters";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Company {
  id: number;
  name: string;
  address: string;
  phone1: number;
  phone2?: number | null;
  gst_number: string;
  email?: string | null;
  created_at: string;
  deliveries?: Delivery[];
}

interface ReceiptDetail {
  id: number;
  counts: number;
  number_of_bags: number;
  weight: number;
  mill?: string;
}

interface ReceivedNoteDetail {
  id: number;
  counts: number;
  number_of_bags: number;
  weight: number;
  mill?: string;
}

interface ReceivedNote {
  id: number;
  company_id: number;
  representative_name?: string;
  representative_phone?: number;
  created_at: string;
  received_note_details: ReceivedNoteDetail[];
  total_weight: number;
  total_bags: number;
  counts_display: string;
  mills_display: string;
  company?: {
    id: number;
    name: string;
  };
}

interface Receipt {
  id: number;
  company_id: number;
  representative_name?: string;
  representative_phone?: number;
  created_at: string;
  receipt_details: ReceiptDetail[];
  total_weight: number;
  total_bags: number;
  counts_display: string;
  mills_display: string;
  company?: {
    id: number;
    name: string;
  };
}

interface ProgramDetail {
  id: number;
  dia: number;
  gauge: number;
  program_type: string;
  weight_in_kilo: number;
  design?: string;
  status?: string;
  completed_weight?: number;
}

interface Program {
  id: number;
  company_id: number;
  lot?: string;
  status: string;
  created_at: string;
  program_details: ProgramDetail[];
  total_weight: number;
  total_completed_weight: number;
  progress_percentage: number;
  specs_display: string;
  dias_display: string;
  gauges_display: string;
  types_display: string;
  designs_display: string;
  company?: {
    id: number;
    name: string;
  };
}

interface DeliveryDetail {
  id: number;
  dia: string;
  gauge: string;
  rolls: number;
  weight: number;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

interface Delivery {
  id: number;
  delivery_person_name: string;
  delivery_person_phone: string;
  delivery_vehicle_number: string;
  created_at: string;
  delivery_details: DeliveryDetail[];
  calculated_total_weight: number;
  calculated_total_rolls: number;
  specs_display: string;
  program: {
    id: number;
    lot?: string;
  };
}

const companySchema = z.object({
  name: z.string().min(1, "Name is required"),
  address: z.string().min(1, "Address is required"),
  phone1: z.number().int("Phone number must be an integer"),
  phone2: z
    .number()
    .int("Phone number must be an integer")
    .nullable()
    .optional(),
  gst_number: z.string().min(1, "GST number is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
});

type CompanyFormData = z.infer<typeof companySchema>;

export default function CompanyDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);
  const [receivedNotes, setReceivedNotes] = useState<ReceivedNote[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loadingReceivedNotes, setLoadingReceivedNotes] = useState(true);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);

  const { control, handleSubmit, reset } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
  });

  useEffect(() => {
    fetchCompany();
    fetchReceivedNotes();
    fetchPrograms();
    fetchDeliveries();
    fetchMachines();
  }, [id]);

  const fetchCompany = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/companies/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch company");
      }

      const data = await response.json();
      setCompany(data);
      reset({
        name: data.name,
        address: data.address,
        phone1: Number(data.phone1),
        phone2: data.phone2 ? Number(data.phone2) : null,
        gst_number: data.gst_number,
        email: data.email || "",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchReceivedNotes = async () => {
    try {
      setLoadingReceivedNotes(true);
      const response = await authenticatedFetch(`${API_URL}/received_notes?per_page=1000`);
      if (!response.ok) {
        throw new Error("Failed to fetch received notes");
      }

      const data = await response.json();
      // Filter received notes by company_id
      const companyReceivedNotes = data.filter(
        (receivedNote: ReceivedNote) => receivedNote.company_id === Number(id)
      );
      setReceivedNotes(companyReceivedNotes);
    } catch (err) {
      console.error("Error fetching received notes:", err);
    } finally {
      setLoadingReceivedNotes(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      setLoadingPrograms(true);
      const response = await authenticatedFetch(`${API_URL}/programs?per_page=1000`);
      if (!response.ok) {
        throw new Error("Failed to fetch programs");
      }

      const data = await response.json();
      // Filter programs by company_id
      const companyPrograms = data.filter(
        (program: Program) => program.company_id === Number(id)
      );
      setPrograms(companyPrograms);
    } catch (err) {
      console.error("Error fetching programs:", err);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const fetchDeliveries = async () => {
    try {
      setLoadingDeliveries(true);
      const response = await authenticatedFetch(`${API_URL}/companies/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch deliveries");
      }

      const data = await response.json();
      // Deliveries are included in the company response
      setDeliveries(data.deliveries || []);
    } catch (err) {
      console.error("Error fetching deliveries:", err);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/machines`);
      if (response.ok) {
        const data = await response.json();
        setMachines(data);
      }
    } catch (err) {
      console.error("Error fetching machines:", err);
    }
  };

  const onSubmit = async (data: CompanyFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/companies/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ company: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      const updatedCompany = await response.json();
      setCompany(updatedCompany);
      setIsEditing(false);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this company?")) {
      try {
        const response = await authenticatedFetch(
          `${API_URL}/companies/${id}`,
          {
            method: "DELETE",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete company");
        }

        navigate("/companies");
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Card sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Typography variant={isMobile ? "h6" : "h5"} component="h2">
              {isEditing ? "Edit Company" : "Company Details"}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/companies")}
              variant="outlined"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Back
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isEditing ? (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Company Name"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="address"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Address"
                    fullWidth
                    margin="normal"
                    multiline
                    rows={3}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="phone1"
                control={control}
                render={({
                  field: { onChange, value, ...field },
                  fieldState,
                }) => (
                  <TextField
                    {...field}
                    value={value || ""}
                    onChange={(e) =>
                      onChange(e.target.value ? Number(e.target.value) : "")
                    }
                    label="Primary Phone"
                    type="tel"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="phone2"
                control={control}
                render={({
                  field: { onChange, value, ...field },
                  fieldState,
                }) => (
                  <TextField
                    {...field}
                    value={value || ""}
                    onChange={(e) =>
                      onChange(e.target.value ? Number(e.target.value) : null)
                    }
                    label="Secondary Phone (Optional)"
                    type="tel"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="gst_number"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="GST Number"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Email (Optional)"
                    type="email"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Stack 
                direction={{ xs: "column", sm: "row" }} 
                spacing={2} 
                sx={{ mt: 3 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  fullWidth
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                  fullWidth
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Company Name
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {company?.name}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Address
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {company?.address}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Primary Phone
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {company?.phone1}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Secondary Phone
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {company?.phone2 || "-"}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                GST Number
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {company?.gst_number}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {company?.email || "-"}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {new Date(company?.created_at || "").toLocaleDateString()}
              </Typography>

              <Stack 
                direction={{ xs: "column", sm: "row" }} 
                spacing={2}
              >
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                  fullWidth
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  fullWidth
                >
                  Delete
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Received Notes Table */}
      <Card sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Received Notes
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {loadingReceivedNotes ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={30} />
            </Box>
          ) : receivedNotes.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>
              No received notes found for this company
            </Typography>
          ) : isMobile ? (
            <Stack spacing={2}>
              {receivedNotes.map((receivedNote) => {
                const weightsPerBag = Array.from(
                  new Set(
                    receivedNote.received_note_details.map((d: ReceivedNoteDetail) => d.weight.toString())
                  )
                ).join(", ");

                return (
                  <Card
                    key={receivedNote.id}
                    variant="outlined"
                    onClick={() => navigate(`/received-notes/${receivedNote.id}`)}
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        boxShadow: 2,
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                          ID #{receivedNote.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(receivedNote.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Counts
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {receivedNote.counts_display}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Total Bags
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatInteger(receivedNote.total_bags)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Weights/Bag
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {weightsPerBag}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Total Weight
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {receivedNote.total_weight} kg
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Mill
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {receivedNote.mills_display}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Representative
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {receivedNote.representative_name || "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Counts</TableCell>
                    <TableCell>Total Bags</TableCell>
                    <TableCell>Weights/Bag</TableCell>
                    <TableCell>Total Weight (kg)</TableCell>
                    <TableCell>Mill</TableCell>
                    <TableCell>Representative</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receivedNotes.map((receivedNote) => {
                    const weightsPerBag = Array.from(
                      new Set(
                        receivedNote.received_note_details.map((d: ReceivedNoteDetail) => d.weight.toString())
                      )
                    ).join(", ");

                    return (
                      <TableRow
                        key={receivedNote.id}
                        hover
                        onClick={() => navigate(`/received-notes/${receivedNote.id}`)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>{receivedNote.id}</TableCell>
                        <TableCell>{receivedNote.counts_display}</TableCell>
                        <TableCell>{formatInteger(receivedNote.total_bags)}</TableCell>
                        <TableCell>{weightsPerBag}</TableCell>
                        <TableCell>{receivedNote.total_weight}</TableCell>
                        <TableCell>{receivedNote.mills_display}</TableCell>
                        <TableCell>
                          {receivedNote.representative_name || "-"}
                        </TableCell>
                        <TableCell>
                          {new Date(receivedNote.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Programs Table */}
      <Card sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Programs
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {loadingPrograms ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={30} />
            </Box>
          ) : programs.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>
              No programs found for this company
            </Typography>
          ) : isMobile ? (
            <Stack spacing={2}>
              {programs.map((program) => (
                <Card
                  key={program.id}
                  variant="outlined"
                  onClick={() => navigate(`/programs/${program.id}`)}
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      boxShadow: 2,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                      <Box sx={{ maxWidth: '60%' }}>
                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                          Program #{program.id}
                        </Typography>
                        {program.lot && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={truncatedTextStyle(PROGRAM_NAME_MAX_WIDTH.mobile)}
                            title={`Lot: ${program.lot}`}
                          >
                            Lot: {truncateText(program.lot, 15)}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={
                          program.status === "not_started"
                            ? "Not Started"
                            : program.status === "in_progress"
                            ? "In Progress"
                            : program.status === "completed"
                            ? "Completed"
                            : "Delivered"
                        }
                        size="small"
                        sx={{
                          bgcolor:
                            program.status === "not_started"
                              ? "#9e9e9e"
                              : program.status === "in_progress"
                              ? "#fdd835"
                              : program.status === "completed"
                              ? "#66bb6a"
                              : "#2196f3",
                          color:
                            program.status === "in_progress"
                              ? "#000"
                              : "#fff",
                        }}
                      />
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Dia
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                          {program.program_details
                            .filter((detail, index, self) => 
                              index === self.findIndex((d) => 
                                d.dia === detail.dia && d.gauge === detail.gauge && d.program_type === detail.program_type
                              )
                            )
                            .map((detail, idx) => (
                              <DiaCircle
                                key={idx}
                                dia={detail.dia}
                                gauge={String(detail.gauge)}
                                machineType={detail.program_type}
                                machines={machines}
                                size="small"
                              />
                            ))}
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Gauge (GG)
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                          {(program.gauges_display || program.specs_display?.split('/')[1]?.replace(/ GG/g, '') || '')
                            .split(", ")
                            .filter(g => g)
                            .map((gauge, idx) => (
                              <GaugeChip
                                key={idx}
                                gauge={gauge}
                              />
                            ))}
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Type
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                          {program.types_display.split(", ").map((type, idx) => (
                            <Chip
                              key={idx}
                              label={type}
                              size="small"
                              sx={{
                                bgcolor: type === "rib" ? '#e0e0e0' : '#757575',
                                color: type === "rib" ? '#000' : '#fff',
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Total Weight
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {program.total_weight} kg
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Created At
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {new Date(program.created_at).toLocaleDateString()}
                        </Typography>
                      </Grid>

                      {/* Progress Bar for in_progress programs */}
                      {program.status === "in_progress" && (
                        <Grid item xs={12}>
                          <ProgressBar
                            type="weight"
                            done={Number(program.total_completed_weight) || 0}
                            total={Number(program.total_weight) || 0}
                            size="small"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Dia</TableCell>
                    <TableCell>Gauge (GG)</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Total Weight (kg)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Lot</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programs.map((program) => (
                    <TableRow
                      key={program.id}
                      hover
                      onClick={() => navigate(`/programs/${program.id}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{program.id}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {program.program_details
                            .filter((detail, index, self) => 
                              index === self.findIndex((d) => 
                                d.dia === detail.dia && d.gauge === detail.gauge && d.program_type === detail.program_type
                              )
                            )
                            .map((detail, idx) => (
                              <DiaCircle
                                key={idx}
                                dia={detail.dia}
                                gauge={String(detail.gauge)}
                                machineType={detail.program_type}
                                machines={machines}
                                size="small"
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(program.gauges_display || program.specs_display?.split('/')[1]?.replace(/ GG/g, '') || '')
                            .split(", ")
                            .filter(g => g)
                            .map((gauge, idx) => (
                              <GaugeChip
                                key={idx}
                                gauge={gauge}
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {program.types_display.split(", ").map((type, idx) => (
                          <Chip
                            key={idx}
                            label={type}
                            size="small"
                            sx={{
                              bgcolor: type === "rib" ? '#e0e0e0' : '#757575',
                              color: type === "rib" ? '#000' : '#fff',
                              mr: 0.5,
                              mb: 0.5
                            }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {program.status === "in_progress" ? (
                          <Box sx={{ minWidth: 160 }}>
                            <ProgressBar
                              type="weight"
                              done={Number(program.total_completed_weight) || 0}
                              total={Number(program.total_weight) || 0}
                              size="small"
                            />
                          </Box>
                        ) : (
                          `${program.total_weight} kg`
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            program.status === "not_started"
                              ? "Not Started"
                              : program.status === "in_progress"
                              ? "In Progress"
                              : program.status === "completed"
                              ? "Completed"
                              : "Delivered"
                          }
                          size="small"
                          sx={{
                            bgcolor:
                              program.status === "not_started"
                                ? "#9e9e9e"
                                : program.status === "in_progress"
                                ? "#fdd835"
                                : program.status === "completed"
                                ? "#66bb6a"
                                : "#2196f3",
                            color:
                              program.status === "in_progress"
                                ? "#000"
                                : "#fff",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: '150px' }}>
                        <Typography 
                          sx={truncatedTextStyle('150px')}
                          title={program.lot || "-"}
                        >
                          {program.lot || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(program.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Deliveries Table */}
      <Card sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 } }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            Deliveries
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {loadingDeliveries ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={30} />
            </Box>
          ) : deliveries.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>
              No deliveries found for this company
            </Typography>
          ) : isMobile ? (
            <>
              <Stack spacing={2}>
                {deliveries.map((delivery) => (
                  <Card
                    key={delivery.id}
                    variant="outlined"
                    onClick={() => navigate(`/deliveries/${delivery.id}`)}
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        boxShadow: 2,
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                          Delivery #{delivery.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(delivery.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Program
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          sx={truncatedTextStyle(PROGRAM_NAME_MAX_WIDTH.mobile)}
                          title={`#${delivery.program.id}${delivery.program.lot ? ` (${delivery.program.lot})` : ''}`}
                        >
                          #{delivery.program.id}
                          {delivery.program.lot && ` (${truncateText(delivery.program.lot, 15)})`}
                        </Typography>
                      </Box>

                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Delivery Person
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {delivery.delivery_person_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {delivery.delivery_person_phone}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Vehicle
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {delivery.delivery_vehicle_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Specs
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {delivery.specs_display}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Rolls
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatInteger(delivery.calculated_total_rolls)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Weight
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {typeof delivery.calculated_total_weight === "number"
                              ? delivery.calculated_total_weight.toFixed(2)
                              : delivery.calculated_total_weight}{" "}
                            kg
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
              
              {/* Mobile Total Card */}
              <Card
                variant="outlined"
                sx={{
                  mt: 2,
                  bgcolor: "action.hover",
                }}
              >
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                    Total
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Total Rolls
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {formatInteger(deliveries.reduce(
                          (sum, delivery) => sum + delivery.calculated_total_rolls,
                          0
                        ))}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Total Weight
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {deliveries
                          .reduce(
                            (sum, delivery) =>
                              sum + Number(delivery.calculated_total_weight),
                            0
                          )
                          .toFixed(2)}{" "}
                        kg
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell>Person</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Specs</TableCell>
                    <TableCell>Rolls</TableCell>
                    <TableCell>Weight (kg)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveries.map((delivery) => (
                    <TableRow
                      key={delivery.id}
                      hover
                      onClick={() => navigate(`/deliveries/${delivery.id}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{delivery.id}</TableCell>
                      <TableCell>
                        {new Date(delivery.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell sx={{ maxWidth: '180px' }}>
                        <Typography 
                          sx={truncatedTextStyle('180px')}
                          title={`Program #${delivery.program.id}${delivery.program.lot ? ` (${delivery.program.lot})` : ''}`}
                        >
                          Program #{delivery.program.id}
                          {delivery.program.lot && ` (${truncateText(delivery.program.lot, 15)})`}
                        </Typography>
                      </TableCell>
                      <TableCell>{delivery.delivery_person_name}</TableCell>
                      <TableCell>{delivery.delivery_person_phone}</TableCell>
                      <TableCell>{delivery.delivery_vehicle_number}</TableCell>
                      <TableCell>{delivery.specs_display}</TableCell>
                      <TableCell>{formatInteger(delivery.calculated_total_rolls)}</TableCell>
                      <TableCell>
                        {typeof delivery.calculated_total_weight === "number"
                          ? delivery.calculated_total_weight.toFixed(2)
                          : delivery.calculated_total_weight}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell colSpan={7}>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell>
                      <strong>
                        {formatInteger(deliveries.reduce(
                          (sum, delivery) => sum + delivery.calculated_total_rolls,
                          0
                        ))}
                      </strong>
                    </TableCell>
                    <TableCell>
                      <strong>
                        {deliveries
                          .reduce(
                            (sum, delivery) =>
                              sum + Number(delivery.calculated_total_weight),
                            0
                          )
                          .toFixed(2)}{" "}
                        kg
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
